/*
 * exchange-bar.js — ANSIN exchange ("wymiana") session bar.
 *
 * ONE piece of state drives every session-aware surface in the theme:
 *
 *   <html class="exchange-session-active" data-exchange-deadline="1786000000000">
 *
 * Three writers, in order of precedence within a page load:
 *   1. layout/theme.liquid  — server-rendered from cart.attributes, so the correct
 *                             bar is in the FIRST paint on every normal page load.
 *   2. inline <head> script — parses ?ex=1 on the landing page, where the cart is
 *                             not stamped yet and Liquid cannot know.
 *   3. sync() below         — authoritative; called by exchange-session.js after it
 *                             has actually read/written the cart.
 *
 * Visibility itself is pure CSS (see section-announcement-bar.css), so no JS runs
 * for a correct first paint. This file only flips the class and ticks the clock.
 *
 * Deliberately separate from exchange-session.js: that file documents itself as
 * theme-portable and is shared with a Dawn port, whereas this bar is ANSIN-specific.
 *
 * Classic (non-module) global IIFE — loaded via <script src defer>.
 */
(() => {
  "use strict";

  const ROOT_CLASS = "exchange-session-active";
  const TICK_MS = 1000;

  const root = document.documentElement;
  let tickerId = null;

  /**
   * Format the remaining milliseconds, in one of two styles:
   *
   *   compact — "2h 00min" / "45min", for the announcement bar
   *   long    — "1 godz. 12 min" / "45 min", for the sentence in the cart bar
   *
   * No seconds (ANSIN 2026-08, review round 2: "bez sekund"). Minutes round UP, so
   * a session that has just started reads "2h 00min" rather than "1h 59min", and the
   * clock never sits at "0min" while free shipping is still live. That errs the same
   * way the Function's hourly floor does — in the customer's favour.
   *
   * Clamped at zero; a negative clock is never shown to a customer.
   *
   * @param {number} ms
   * @param {"compact"|"long"} [style]
   * @returns {string}
   */
  function formatRemaining(ms, style) {
    const totalMinutes = Math.max(0, Math.ceil(ms / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (style === "long") {
      // Unpadded, and the minutes drop out entirely on the hour: this one sits inside
      // a sentence, where "1 godz. 05 min" reads oddly and "2 godz. 0 min" worse.
      if (hours > 0) return minutes > 0 ? `${hours} godz. ${minutes} min` : `${hours} godz.`;
      return `${minutes} min`;
    }
    return hours > 0 ? `${hours}h ${String(minutes).padStart(2, "0")}min` : `${minutes}min`;
  }

  /** Current deadline as read from the root element, or NaN. */
  const currentDeadline = () => Number(root.dataset.exchangeDeadline);

  /**
   * Paint the countdown into every slot. Recomputed from Date.now() on each call,
   * so a throttled background tab loses resolution, never accuracy.
   */
  function paintCountdown() {
    const deadline = currentDeadline();
    const hasDeadline = Number.isFinite(deadline);

    // No deadline means there is no clock to show. Without this the bar renders
    // its label ("Pozostało") next to a permanently empty, width-reserved slot,
    // which reads as a broken bar rather than as a bar without a countdown.
    document.querySelectorAll("[data-exchange-countdown]").forEach((el) => {
      el.hidden = !hasDeadline;
    });
    if (!hasDeadline) return;

    const remaining = deadline - Date.now();
    document.querySelectorAll("[data-exchange-countdown-value]").forEach((el) => {
      // The cart bar's slot asks for the long style; the bar's own asks for compact.
      // Defaulting keeps any slot that predates the attribute working.
      el.textContent = formatRemaining(remaining, el.dataset.exchangeCountdownValue);
    });
  }

  function stopTicking() {
    if (tickerId !== null) {
      clearInterval(tickerId);
      tickerId = null;
    }
  }

  function startTicking() {
    stopTicking();
    paintCountdown();
    tickerId = setInterval(() => {
      paintCountdown();
      // Stop at zero but leave the bar up: expiry belongs to exchange-session.js,
      // which clears the cart attributes and then calls sync(null). Hiding the bar
      // here would contradict a cart that still grants free shipping.
      if (currentDeadline() - Date.now() <= 0) stopTicking();
    }, TICK_MS);
  }

  /**
   * THE entry point. Idempotent and synchronous.
   *
   * @param {number|null} deadlineMs Epoch ms. null/NaN/past restores the normal bar.
   * @returns {boolean} Whether the exchange bar is showing after this call.
   */
  function sync(deadlineMs) {
    const ms = Number(deadlineMs);
    // Strict `<`, matching cartSessionActive() in exchange-session.js and the
    // shipping bar in global.js. Three modules disagreeing by a millisecond is a
    // real source of "the bar says one thing, the cart says another".
    const active = Number.isFinite(ms) && Date.now() < ms;
    const was = root.classList.contains(ROOT_CLASS);

    root.classList.toggle(ROOT_CLASS, active);
    if (active) {
      root.dataset.exchangeDeadline = String(ms);
      startTicking();
    } else {
      delete root.dataset.exchangeDeadline;
      stopTicking();
    }

    // Swapping the bar can change the header offset. global.js recomputes
    // --announcement-bars-before-header-heights only on scroll/resize, so without
    // this nudge the sticky header and every calc() on that variable go stale.
    if (was !== active) {
      requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    }

    return active;
  }

  /** @returns {boolean} */
  const isActive = () => root.classList.contains(ROOT_CLASS);

  /**
   * Adopt whatever the server (or the pre-paint hint) already put on <html>, so the
   * countdown starts immediately instead of waiting for exchange-session.js to
   * finish its /cart.js round trip.
   */
  function adoptRootState() {
    if (isActive() && Number.isFinite(currentDeadline())) {
      startTicking();
    } else {
      stopTicking();
      paintCountdown(); // collapses the slot when there is no deadline to show
    }
  }

  // paintCountdown is exported so a surface that builds a countdown slot after the
  // ticker is already running (the cart drawer, on open) can fill it immediately
  // instead of showing an empty gap until the next tick.
  window.ANSIN_EXCHANGE_BAR = { sync, isActive, paintCountdown };

  // There is no manual exit any more (ANSIN 2026-08, review round 4 — see the
  // snippet for why, and for how to put it back). The delegated
  // [data-exchange-end] listener that used to live here went with the button;
  // window.ANSIN_EXCHANGE_SESSION.end() is still exported by exchange-session.js
  // and is still the only safe teardown, so restoring it is a three-line change.

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", adoptRootState);
  } else {
    adoptRootState();
  }

  // A frozen/throttled tab can drift far from the real clock; repaint the moment
  // it comes back rather than waiting up to a second.
  window.addEventListener("pageshow", paintCountdown);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") paintCountdown();
  });
  // Theme editor re-renders the header group without a page load.
  document.addEventListener("shopify:section:load", adoptRootState);
})();
